import React from 'react';
import { defineMessages } from 'react-intl';
import type { IntlShape } from 'react-intl';
import type { EditorState } from 'prosemirror-state';

import { copyToClipboard } from '@atlaskit/editor-common/clipboard';
import LinkIcon from '@atlaskit/icon/core/link';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import type { ExtractInjectionAPI } from '@atlaskit/editor-common/types/next-editor-plugin';
import type { Command } from '@atlaskit/editor-common/types/command';
import type {
	FloatingToolbarItem,
	FloatingToolbarConfig,
} from '@atlaskit/editor-common/types/floating-toolbar';
import type { Node } from '@atlaskit/editor-prosemirror/model';
import { NodeSelection } from '@atlaskit/editor-prosemirror/state';
import {
	ACTION,
	ACTION_SUBJECT,
	ACTION_SUBJECT_ID,
	EVENT_TYPE,
	INPUT_METHOD,
} from '@atlaskit/editor-common/analytics/types/enums';
import type { EditorAnalyticsAPI } from '@atlaskit/editor-common/analytics/api';

import { VIEW_PAGE, COMPANY_HUB } from '@confluence/named-routes/entry-points/routes.ts';
import { getMonitoringClient } from '@confluence/monitoring/entry-points/getMonitoringClient.ts';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { isCompanyHubSpaceKey } from '@confluence/route-manager/entry-points/companyHubUtils.ts';

import { pluginKey } from '../../createPlugin.ts';
import type { ExtensionManagerPlugin } from '../../createPlugin.ts';
import type { ToolbarOverride } from '../toolbarConfigTypes.ts';

// eslint-disable-next-line import/no-cycle
import { CopyLinkToolbarItemTooltipContent } from './components/CopyLinkToolbarItemTooltipContent.tsx';

const i18n = defineMessages({
	anchorMacroCopyLinkCTALabel: {
		id: 'fabric-extension-manager-plugin.anchor.copy-link-button.label',
		description: 'Label for the copy link button in the anchor macro floating toolbar',
		defaultMessage: 'Copy link',
	},
});

export const checkIfValidAnchorName = (node: Node): boolean => {
	const rawAnchorName: string | undefined = node.attrs.parameters.macroParams['']?.value;
	// Normalize TinyMCE-era anchor names: strip spaces to match legacy URL format.
	let anchorName = rawAnchorName;
	if (typeof rawAnchorName === 'string') {
		anchorName = rawAnchorName.replaceAll(/\s+/g, '');
	}

	if (!anchorName) return false;

	try {
		decodeURIComponent(anchorName);
		return true;
	} catch {
		return false;
	}
};

const getPathName = (isLivePage?: boolean, contentId?: string, spaceKey?: string) => {
	const isCompanyHub = isCompanyHubSpaceKey(spaceKey);
	if (isCompanyHub) {
		return COMPANY_HUB.toUrl();
	}

	if (isLivePage) {
		return typeof window !== 'undefined' ? window.location.pathname : '';
	}

	return VIEW_PAGE.toUrl({ contentId, spaceKey });
};

const encodeAnchorNameForUrlFragment = (anchorName: string): string => {
	// Encode as a fragment value, but keep `/` readable for path-like anchor names.
	return encodeURIComponent(anchorName).replace(/%2F/gi, '/');
};

export const copyAnchorLinkCommand =
	(node: Node, fireAnalyticsEvent?: EditorAnalyticsAPI['fireAnalyticsEvent']): Command =>
	(state, dispatch) => {
		const { tr } = state;
		const { isLivePage, contentId, spaceKey } = pluginKey.getState(state)?.pageContext ?? {};

		const rawAnchorName: string | undefined = node.attrs.parameters.macroParams['']?.value;
		// Normalize TinyMCE-era anchor names: strip spaces to match legacy URL format.
		let anchorName = rawAnchorName;
		if (typeof rawAnchorName === 'string') {
			anchorName = rawAnchorName.replaceAll(/\s+/g, '');
		}

		const pathName = getPathName(isLivePage, contentId, spaceKey);

		const setShowAnchorLinkCopiedTooltip = (showAnchorLinkCopiedTooltip: boolean) => {
			dispatch?.(
				tr.setMeta(pluginKey, {
					showAnchorLinkCopiedTooltip,
				}),
			);
		};

		fireAnalyticsEvent?.({
			action: ACTION.CLICKED,
			actionSubject: ACTION_SUBJECT.BUTTON,
			actionSubjectId: ACTION_SUBJECT_ID.COPY_LINK_TO_ANCHOR,
			eventType: EVENT_TYPE.UI,
			attributes: {
				inputMethod: INPUT_METHOD.FLOATING_TB,
				extensionKey: node.attrs?.extensionKey,
				extensionType: node.attrs?.extensionType,
				isLivePage,
			},
		});

		if (dispatch) {
			try {
				decodeURIComponent(anchorName ?? '');
				const anchorNameForUrlFragment = fg('cc_page_experiences_anchor_encode')
					? encodeAnchorNameForUrlFragment(anchorName ?? '')
					: (anchorName ?? '');

				copyToClipboard(
					`${typeof window !== 'undefined' ? window.location.origin : ''}${pathName}#${anchorNameForUrlFragment}`,
				)
					.then(() => {
						setShowAnchorLinkCopiedTooltip(true);
					})
					.catch((error) => {
						getMonitoringClient().submitError(error, {
							attribution: Attribution.PAGE_EXTENSIONS,
						});

						setShowAnchorLinkCopiedTooltip(false);
					});
			} catch {
				setShowAnchorLinkCopiedTooltip(false);
			}
		}
		return true;
	};

export const overrideToolbarConfigForAnchorMacro = (
	toolbarItems: FloatingToolbarItem<Command>[],
	intl: IntlShape,
	node: Node,
	api?: ExtractInjectionAPI<ExtensionManagerPlugin>,
): FloatingToolbarItem<Command>[] => {
	const updatedToolbarItems = toolbarItems.slice();
	const indexOfFirstSeperator = toolbarItems.map((item) => item.type).indexOf('separator');
	const { fireAnalyticsEvent } = api?.analytics?.actions ?? {};
	const newItemsToInsert: FloatingToolbarItem<Command>[] = [
		{
			id: 'editor.anchor.macro.copy-link',
			type: 'button',
			title: intl.formatMessage(i18n.anchorMacroCopyLinkCTALabel),
			tooltipContent: ({ update }) => (
				<CopyLinkToolbarItemTooltipContent
					node={node}
					intl={intl}
					api={api}
					updateTooltipPosition={update}
				/>
			),
			showTitle: true,
			hideTooltipOnClick: false,
			icon: LinkIcon,
			onClick: copyAnchorLinkCommand(node, fireAnalyticsEvent),
			disabled: !checkIfValidAnchorName(node),
		},
	];
	/**
	 * If there is no separator, 1 group present, add
	 * separator before the new items to create a new visual
	 */
	if (indexOfFirstSeperator === -1) {
		newItemsToInsert.unshift({
			type: 'separator',
			hidden: false,
		});
	} /**
	 * If there is a separator in the toolbar items, and its not the last or first item
	 * add the new items after that separator to create new visual for our newitems
	 *
	 * NOTE: We don't want to add another separator if the separator is the last or first item,
	 * which it should never be because it doesn't make sense, but just in case we will check for that
	 */ else if (indexOfFirstSeperator !== 0 && indexOfFirstSeperator !== toolbarItems.length - 1) {
		newItemsToInsert.push({
			type: 'separator',
			hidden: false,
		});
	}
	updatedToolbarItems.splice(indexOfFirstSeperator + 1, 0, ...newItemsToInsert);
	return updatedToolbarItems;
};

export const anchorMacroToolbarOverride: ToolbarOverride = {
	shouldApplyOverride: (state: EditorState, config: FloatingToolbarConfig) => {
		const isCurrentSelectionAnchorMacro =
			state.selection instanceof NodeSelection &&
			state.selection.node.attrs.extensionType === 'com.atlassian.confluence.macro.core' &&
			state.selection.node.attrs.extensionKey === 'anchor';

		return (
			isCurrentSelectionAnchorMacro &&
			(Array.isArray(config.nodeType)
				? config.nodeType.includes(state.schema.nodes.inlineExtension)
				: config.nodeType === state.schema.nodes.inlineExtension)
		);
	},
	applyOverride: (
		config: FloatingToolbarConfig,
		intl: IntlShape,
		api?: ExtractInjectionAPI<ExtensionManagerPlugin>,
	) => {
		if (Array.isArray(config.items)) {
			const toolbarItems = config.items;
			config.items = (node: Node) => {
				if (
					node.attrs.extensionType === 'com.atlassian.confluence.macro.core' &&
					node.attrs.extensionKey === 'anchor'
				) {
					return overrideToolbarConfigForAnchorMacro(toolbarItems, intl, node, api);
				}
				return toolbarItems;
			};
		}
	},
};
